import { useEffect, useState } from 'preact/hooks';
import { PlatformProvider } from '@vibeshell/components';
import { PromptInput, type GenerationMode } from './components/PromptInput';
import { GenerationStream } from './components/GenerationStream';
import { HomeV2 } from './v2/HomeV2';
import { EditorWorkspace } from './components/EditorWorkspace';
import { InstallCoachUI } from './components/InstallCoach';
import { AuthWall } from './components/AuthWall';
import { TopupReturnCapture } from './components/PomelaCheckout';
import { CelebrationOverlay } from './components/CelebrationOverlay';
import { getDeviceId } from './lib/deviceId';
import { t } from './lib/i18n';
import { useGenerationSession, type GenerationState } from './lib/useGenerationSession';

export type { GenerationState };

export function App({ editAppId }: { editAppId?: string } = {}) {
  // The freemium auth wall — raised when the gateway 403s a guest who has
  // used their free generations.
  const [showAuthWall, setShowAuthWall] = useState(false);
  // §F02 — the just-submitted draft carried up with the wall, so AuthWall can
  // stash it for the sign-in round trip (undefined for a revise).
  const [authWallDraft, setAuthWallDraft] =
    useState<{ prompt: string; mode: GenerationMode } | undefined>(undefined);
  // First-build celebration latch — the hook signals via onFirstBuild;
  // App owns the overlay render so it can be dismissed independently.
  const [celebrateFirstBuild, setCelebrateFirstBuild] = useState(false);

  // The generation state machine lives in useGenerationSession — see
  // apps/creator/src/lib/useGenerationSession.ts. App.tsx now owns only
  // UI concerns (sidebar swipe, telemetry pings, render).
  // (NIGHT_AUDIT §2.9.)
  const session = useGenerationSession({
    onAuthWall: (draft) => { setAuthWallDraft(draft); setShowAuthWall(true); },
    onFirstBuild: () => setCelebrateFirstBuild(true)
  });
  const { state } = session;

  // §S680 — the ADDRESSABLE editor route (#apps/:id/edit). CreatorRoot hands
  // the id down as a prop; consume it exactly once per id. openApp restores
  // from local recents, falls back to the server row, or honestly lands on
  // the Workspace list — so the editor now survives a reload. The legacy
  // sessionStorage 'vs:creator:open-app' consume path (inside the hook's
  // mount effect) keeps working for callers that still use it.
  useEffect(() => {
    if (editAppId) void session.openApp(editAppId);
    // session.openApp is a stable useCallback identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editAppId]);

  // §S680 — the CommandPalette's "Start a new project" fires this event so
  // picking it while the hash is ALREADY '' (or while the App instance stays
  // mounted across the hash flip from #apps/:id/edit) still resets to a
  // fresh composer instead of being a silent no-op. cancel() == reset() +
  // abort of any in-flight stream, which is what "new project" means.
  useEffect(() => {
    const onNewProject = (): void => session.cancel();
    window.addEventListener('pomela:new-project', onNewProject);
    return () => window.removeEventListener('pomela:new-project', onNewProject);
    // session.cancel is a stable useCallback identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-tab telemetry — fire once per session.
  useEffect(() => {
    window.posthog?.capture('creator_session_view');
    const SESSION_PING_KEY = 'vs:creator:session-pinged';
    let alreadyPinged = false;
    try {
      alreadyPinged = sessionStorage.getItem(SESSION_PING_KEY) === '1';
      if (!alreadyPinged) sessionStorage.setItem(SESSION_PING_KEY, '1');
    } catch {
      /* sessionStorage blocked — fall through and ping (degrade safely) */
    }
    if (alreadyPinged) return;
    void fetch('/v1/events', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ type: 'session_started', deviceId: getDeviceId() })
    }).catch(() => {});
  }, []);

  return (
    <PlatformProvider>
      <div className="relative min-h-[100dvh]">
        {state.status === 'idle' ? (
          /* §V2 EXACT PORT — the spike's cockpit home (composer as the hero
             element, sparks, real recents). Renders
             inside the V2Shell canvas; the shell owns all chrome. */
          <HomeV2
            onPrompt={(p, mode, assets) => void session.submit(p, mode, assets)}
            seed={session.promptSeed}
          />
        ) : state.status === 'done' || state.status === 'revising' ? (
          /* §V2 exact port — the editor IS the spike's .screen.editor: a
             full-bleed flex column (ed-top + ed-body own the layout; no
             width wrapper). */
          <section className="screen on editor" data-screen="editor">
            <EditorWorkspace
              app={state.status === 'done' ? state.app : state.base}
              events={state.events}
              isRevising={state.status === 'revising'}
              // §S680 — when the editor was reached via #apps/:id/edit,
              // "create another" must also clear the route, or a reload
              // would resurrect the app the user just left.
              onAnother={() => {
                session.reset();
                if (editAppId) location.hash = '';
              }}
              onRevise={session.revise}
              onCancelRevise={session.cancel}
              notice={state.status === 'done' ? state.notice : undefined}
              onClearNotice={session.clearNotice}
              capabilityNote={state.status === 'done' ? state.capabilityNote : undefined}
            />
          </section>
        ) : (
          <section
            className="screen on"
            data-screen={state.status === 'streaming' ? 'genwait' : 'generror'}
          >
            <div
              className={`mx-auto flex flex-col ${
                // §V2: the streaming "Quiet Foundry" self-centers at the
                // spike's 1060px; only the error surface keeps the focused
                // single column.
                state.status === 'streaming' ? 'w-full' : 'max-w-md'
              }`}
            >
              <main className="flex-1 pb-16">
                {state.status === 'streaming' && (
                  <GenerationStream
                    prompt={state.prompt}
                    appName={state.appName}
                    queueInfo={state.queueInfo}
                    attachments={state.attachments}
                    events={state.events}
                    mode={state.mode}
                    startedAt={state.startedAt}
                    onCancel={session.cancel}
                    onAbort={session.abortStream}
                    onRetry={session.retryLast}
                  />
                )}

                {state.status === 'error' && (
                  <ErrorPanel
                    message={state.message}
                    retryable={state.retryable}
                    // A RETRYABLE failure's button says "Try again" — so it must
                    // actually re-run the SAME prompt (retryLast re-submits
                    // lastSubmission), NOT reset to an empty composer (submit
                    // clears promptSeed, so reset would drop the user's typed idea
                    // and force a full re-type). Non-retryable → "Start over" → reset.
                    onReset={state.retryable ? session.retryLast : session.reset}
                  />
                )}
              </main>
            </div>
          </section>
        )}

        {showAuthWall && <AuthWall draft={authWallDraft} onClose={() => setShowAuthWall(false)} />}

        {/* First-build dopamine pop — auto-dismisses after a few seconds,
            or on tap. Latched by localStorage so it only ever fires once
            per device. */}
        {celebrateFirstBuild && (
          <CelebrationOverlay onDismiss={() => setCelebrateFirstBuild(false)} />
        )}

        {/* Suppress the install coach on the home route — the prompt dock
            is a fixed bottom element in the same z-band, so they must not
            stack. The coach reappears on every other surface. */}
        <InstallCoachUI suppressed={state.status === 'idle'} />
        {/* §LG2 — completes a redirect-fallback PayPal top-up on return (?pptopup=…&token=orderId) */}
        <TopupReturnCapture />
      </div>
    </PlatformProvider>
  );
}

function ErrorPanel({
  message,
  retryable,
  onReset
}: {
  message: string;
  retryable: boolean;
  onReset: () => void;
}) {
  return (
    <div className="surface flex flex-col gap-3 border-l-2 border-l-[color:var(--color-danger)] p-5 animate-vs-fade-in">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-danger)]">
        {t('err.genFailed')}
      </p>
      <p className="text-[14px] leading-relaxed text-[color:var(--fg)]">{message}</p>
      <button type="button" onClick={onReset} className="btn btn-secondary self-start">
        {retryable ? t('common.tryAgain') : t('common.startOver')}
      </button>
    </div>
  );
}

